/**
 * WebSession — one browser voice conversation over a Session ws_url.
 *
 * The developer never sees PCM, base64, or wire frame names: audio handling
 * lives behind the AudioEngine seam, and this class only speaks the
 * documented wire subset (send `audio` frames; receive `media` / `clear` /
 * `end_call` / transcript events). Unknown wire events are ignored for
 * forward compatibility.
 */

export type SessionStatus =
  | 'connecting'
  | 'active'
  | { state: 'ended'; reason: string };

export interface TranscriptEvent {
  role: 'user' | 'agent';
  text: string;
  final: boolean;
}

export interface StartOptions {
  /** The ws_url returned by POST /sessions/create. Connect to it as-is. */
  wsUrl: string;
}

type EventMap = {
  status: SessionStatus;
  transcript: TranscriptEvent;
  error: Error;
};

/** Minimal WebSocket surface the session needs (real or test double). */
export interface WebSocketLike {
  send(data: string): void;
  close(code?: number): void;
  onopen: (() => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

/** Audio seam: mic capture in, playback queue out. Hardware lives behind it. */
export interface AudioEngineLike {
  start(onChunk: (base64Pcm: string) => void): Promise<void>;
  enqueue(base64Pcm: string): void;
  clear(): void;
  setMuted(muted: boolean): void;
  stop(): void;
}

/** Internal construction seams for tests; not part of the public surface. */
export interface SessionInternals {
  createWebSocket?: (url: string) => WebSocketLike;
  audioEngine?: AudioEngineLike;
}

const RECONNECT_DELAY_MS = 1000;
// A reconnected socket must stay open at least this long before it counts as
// stable and re-earns the single-retry budget.
const STABLE_MS = 5000;

// end_call wire reasons the SDK renames for the public surface.
const REASON_MAP: Record<string, string> = {
  user_limit_reached: 'insufficient_balance',
};

export class WebSession {
  private listeners: { [K in keyof EventMap]: Array<(v: EventMap[K]) => void> } = {
    status: [],
    transcript: [],
    error: [],
  };

  private ws: WebSocketLike | null = null;
  private audio: AudioEngineLike | null = null;
  private wsUrl = '';
  private status: SessionStatus = { state: 'ended', reason: 'not_started' };
  private muted = false;
  private stopping = false;
  private retried = false;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private startSettle: { resolve: () => void; reject: (e: Error) => void } | null = null;
  private internals: SessionInternals;

  constructor(internals: SessionInternals = {}) {
    this.internals = internals;
  }

  on<K extends keyof EventMap>(event: K, cb: (value: EventMap[K]) => void): this {
    this.listeners[event].push(cb);
    return this;
  }

  off<K extends keyof EventMap>(event: K, cb: (value: EventMap[K]) => void): this {
    const list = this.listeners[event];
    const i = list.indexOf(cb);
    if (i >= 0) list.splice(i, 1);
    return this;
  }

  private emit<K extends keyof EventMap>(event: K, value: EventMap[K]): void {
    for (const cb of [...this.listeners[event]]) cb(value);
  }

  private setStatus(status: SessionStatus): void {
    this.status = status;
    this.emit('status', status);
  }

  async start(options: StartOptions): Promise<void> {
    if (this.ws) throw new Error('WebSession already started');
    this.wsUrl = options.wsUrl;
    this.stopping = false;
    this.retried = false;
    this.setStatus('connecting');

    try {
      this.audio = this.internals.audioEngine ?? (await createDefaultAudioEngine());
      await this.audio.start((chunk) => {
        // Only while live: send() on a CONNECTING socket throws, and chunks
        // captured during the reconnect window must be dropped, not queued.
        if (!this.muted && this.status === 'active') {
          this.ws?.send(JSON.stringify({ type: 'audio', data: chunk }));
        }
      });
      this.audio.setMuted(this.muted);

      await new Promise<void>((resolve, reject) => {
        this.startSettle = { resolve, reject };
        this.connect();
      });
    } catch (err) {
      // Mic acquisition or the initial connect failed: release the mic and
      // leave the session in a clean, restartable ended state rather than
      // stuck at 'connecting' with the stream still open.
      this.startSettle = null;
      this.audio?.stop();
      this.audio = null;
      this.ws = null;
      this.setStatus({ state: 'ended', reason: 'start_failed' });
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  private connect(): void {
    const make =
      this.internals.createWebSocket ??
      ((url: string) => new WebSocket(url) as unknown as WebSocketLike);
    const ws = make(this.wsUrl);
    this.ws = ws;

    ws.onopen = () => {
      this.setStatus('active');
      this.startSettle?.resolve();
      this.startSettle = null;
      // Only a connection that STAYS open earns a fresh retry budget.
      // Resetting on `onopen` alone would loop forever against a server that
      // accepts then immediately closes (e.g. a rejected/dead token: the WS
      // route accepts before validating), because every open re-armed the
      // retry. Requiring stability bounds a dead token to two attempts.
      this.clearStableTimer();
      this.stableTimer = setTimeout(() => {
        this.retried = false;
      }, STABLE_MS);
    };
    ws.onmessage = (ev) => this.handleMessage(ev.data);
    ws.onerror = () => {
      // The close handler decides what happens; onerror alone is noise.
    };
    ws.onclose = () => {
      this.clearStableTimer();
      if (this.stopping || typeof this.status === 'object') return;
      if (!this.retried) {
        // Unexpected close mid-call: the Session Token re-attaches to the
        // same call (ADR-0017), so retry the same wsUrl exactly once.
        this.retried = true;
        this.setStatus('connecting');
        setTimeout(() => {
          if (this.stopping || typeof this.status === 'object') return;
          this.connect();
        }, RECONNECT_DELAY_MS);
        return;
      }
      this.endLocally('connection_lost');
    };
  }

  private clearStableTimer(): void {
    if (this.stableTimer !== null) {
      clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let msg: any;
    try {
      msg = JSON.parse(raw);
    } catch {
      return; // non-JSON frames are not part of the contract — ignore
    }
    const payload = msg?.media?.payload;
    switch (msg?.event) {
      case 'media':
        if (typeof payload === 'string') this.audio?.enqueue(payload);
        break;
      case 'clear':
        this.audio?.clear(); // barge-in: drop queued playback immediately
        break;
      case 'end_call': {
        const wire = typeof payload?.reason === 'string' ? payload.reason : 'hangup';
        this.endLocally(REASON_MAP[wire] ?? wire);
        break;
      }
      case 'user':
        this.emitTranscript('user', payload, true);
        break;
      case 'partial_text':
        this.emitTranscript('user', payload, false);
        break;
      case 'system':
        this.emitTranscript('agent', payload, false);
        break;
      case 'last_system_message':
        // Cumulative snapshot of the agent's utterance so far; each one
        // supersedes the previous agent transcript for the same turn.
        this.emitTranscript('agent', payload, true);
        break;
      case 'error':
        this.emit('error', new Error(String(msg?.message ?? payload ?? 'error')));
        break;
      default:
        break; // forward compatibility: unknown events are ignored
    }
  }

  private emitTranscript(role: 'user' | 'agent', text: unknown, final: boolean): void {
    if (typeof text !== 'string' || text === '') return;
    this.emit('transcript', { role, text, final });
  }

  private endLocally(reason: string): void {
    if (typeof this.status === 'object' && this.status.state === 'ended'
        && this.status.reason !== 'not_started') return;
    this.stopping = true;
    this.clearStableTimer();
    this.startSettle?.reject(new Error(reason));
    this.startSettle = null;
    try {
      this.ws?.close();
    } catch {
      /* already closed */
    }
    // Drop the socket + engine refs so the same instance can start() again
    // with a fresh ws_url (start()'s `if (this.ws)` guard would otherwise
    // reject every reuse after the first hangup).
    this.ws = null;
    this.audio?.stop();
    this.audio = null;
    this.setStatus({ state: 'ended', reason });
  }

  mute(muted: boolean): void {
    this.muted = muted;
    this.audio?.setMuted(muted);
  }

  /** Hang up: closing the socket ends the conversation server-side. */
  stop(): void {
    this.endLocally('stopped');
  }
}

async function createDefaultAudioEngine(): Promise<AudioEngineLike> {
  const { AudioEngine } = await import('./audio');
  return new AudioEngine();
}
